import { useState } from "react";
import "../../components/Modal.css";
import "./RateFormModal.css";
import {
  EnergyType,
  MileageRate,
  RateFormErrors,
  SELECTABLE_ENERGY_TYPES,
  SelectableEnergyType,
  VEHICLE_TYPES,
  VehicleType,
} from "../types";
import { validateRate } from "./utils";

function RateFormModal({
  initialRate,
  isSaving,
  mt,
  vehicleTypeLabels,
  energyTypeLabels,
  onSubmit,
  onClose,
}: {
  initialRate: Partial<MileageRate>;
  isSaving: boolean;
  mt: any;
  vehicleTypeLabels: Record<VehicleType, string>;
  energyTypeLabels: Record<SelectableEnergyType, string>;
  onSubmit: (rate: Partial<MileageRate>) => void;
  onClose: () => void;
}) {
  const [rate, setRate] = useState<Partial<MileageRate>>(() => ({
    ...initialRate,
    power_cv_min: Number(initialRate.power_cv_min ?? 0),
    power_cv_max: Number(initialRate.power_cv_max ?? 0),
    distance_min: Number(initialRate.distance_min ?? 0),
    distance_max: Number(initialRate.distance_max ?? 0),
    fixed_amount: Number(initialRate.fixed_amount ?? 0),
    per_km_amount: Number(initialRate.per_km_amount ?? 0),
  }));
  const [errors, setErrors] = useState<RateFormErrors>({});
  const isEditing = Boolean(initialRate.uuid);

  const update = <K extends keyof MileageRate>(key: K, value: MileageRate[K]) => {
    setRate((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateRate(rate, mt);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSubmit(rate);
  };

  const previewAmount = (
    (rate.fixed_amount ?? 0) + 100 * (rate.per_km_amount ?? 0)
  ).toFixed(2);

  const previewText = (
    mt.previewLabel ?? "Example: for {distance} km, reimbursement ≈ {amount} €"
  )
    .replace("{distance}", "100")
    .replace("{amount}", previewAmount);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {isEditing ? mt.modalEditTitle ?? "Edit rate" : mt.modalCreateTitle ?? "New rate"}
          </h3>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={mt.modalClose ?? "Close"}
          >
            ×
          </button>
        </div>

        <form className="mileage-rate-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>{mt.fieldYear ?? "Year"}</label>
              <input
                type="number"
                value={rate.year ?? ""}
                onChange={(e) => update("year", Number(e.target.value))}
                required
              />
              {errors.year && <span className="field-error">{errors.year}</span>}
            </div>
            <div className="form-group">
              <label>{mt.fieldVehicleType ?? "Vehicle type"}</label>
              <select
                value={rate.vehicle_type}
                onChange={(e) => update("vehicle_type", e.target.value as VehicleType)}
                required
              >
                {VEHICLE_TYPES.map((v) => (
                  <option key={v} value={v}>
                    {vehicleTypeLabels[v]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{mt.fieldEnergyType ?? "Energy"}</label>
              <select
                value={
                  rate.energy_type === "HYBRID" || rate.energy_type === "HYDROGEN"
                    ? "THERMAL"
                    : rate.energy_type ?? ""
                }
                onChange={(e) =>
                  update("energy_type", (e.target.value || null) as EnergyType | null)
                }
              >
                {SELECTABLE_ENERGY_TYPES.map((en) => (
                  <option key={en} value={en}>
                    {energyTypeLabels[en]}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{mt.fieldStatus ?? "Status"}</label>
              <label className="inline-toggle">
                <input
                  type="checkbox"
                  checked={rate.is_active ?? true}
                  onChange={(e) => update("is_active", e.target.checked)}
                />
                <span>
                  {rate.is_active ?? true
                    ? mt.statusActive ?? "Active"
                    : mt.statusInactive ?? "Inactive"}
                </span>
              </label>
            </div>
          </div>

          <div className="form-section-label">{mt.sectionPower ?? "Fiscal power (CV)"}</div>
          <div className="form-row">
            <div className="form-group">
              <label>{mt.fieldMin ?? "Min"}</label>
              <input
                type="number"
                min="0"
                step="1"
                value={rate.power_cv_min ?? ""}
                onChange={(e) => update("power_cv_min", Number(e.target.value))}
                required
              />
              {errors.power_cv_min && <span className="field-error">{errors.power_cv_min}</span>}
            </div>
            <div className="form-group">
              <label>{mt.fieldMax ?? "Max"}</label>
              <input
                type="number"
                min="0"
                step="1"
                value={rate.power_cv_max ?? ""}
                onChange={(e) => update("power_cv_max", Number(e.target.value))}
                required
              />
              {errors.power_cv_max && <span className="field-error">{errors.power_cv_max}</span>}
            </div>
          </div>

          <div className="form-section-label">{mt.sectionDistance ?? "Distance traveled (km)"}</div>
          <div className="form-row">
            <div className="form-group">
              <label>{mt.fieldMin ?? "Min"}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={rate.distance_min ?? ""}
                onChange={(e) => update("distance_min", Number(e.target.value))}
                required
              />
              {errors.distance_min && <span className="field-error">{errors.distance_min}</span>}
            </div>
            <div className="form-group">
              <label>{mt.fieldMax ?? "Max"}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={rate.distance_max ?? ""}
                onChange={(e) => update("distance_max", Number(e.target.value))}
                required
              />
              {errors.distance_max && <span className="field-error">{errors.distance_max}</span>}
            </div>
          </div>

          <div className="form-section-label">{mt.sectionAmounts ?? "Amounts"}</div>
          <div className="form-row">
            <div className="form-group">
              <label>{mt.fixedAmount ?? "Fixed amount (€)"}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={rate.fixed_amount ?? ""}
                onChange={(e) => update("fixed_amount", Number(e.target.value))}
                required
              />
              {errors.fixed_amount && <span className="field-error">{errors.fixed_amount}</span>}
            </div>
            <div className="form-group">
              <label>{mt.perKmAmount ?? "Amount per km (€)"}</label>
              <input
                type="number"
                min="0"
                step="0.0001"
                value={rate.per_km_amount ?? ""}
                onChange={(e) => update("per_km_amount", Number(e.target.value))}
                required
              />
              {errors.per_km_amount && <span className="field-error">{errors.per_km_amount}</span>}
            </div>
          </div>

          <div className="rate-preview">{previewText}</div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              {mt.cancel ?? "Cancel"}
            </button>
            <button type="submit" className="save-settings-btn" disabled={isSaving}>
              {isSaving
                ? mt.saving ?? "Saving..."
                : isEditing
                  ? mt.updateRate ?? "Update"
                  : mt.saveRate ?? "Create rate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RateFormModal;